import { useMeetingEditor } from "./MeetingEditorContext";
import {
  useImmediateMutationSystem,
  type Mutator,
} from "@/lib/queuedMutations";
import { DndContext, type DragEndEvent, pointerWithin } from "@dnd-kit/core";
import {
  type DragData,
  dragInsertPos,
  type DragInsertPos,
  EditorDragOverlay,
} from "./dragDrop";
import { arrayMove } from "@dnd-kit/sortable";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { ArrowDownIcon, PlusIcon } from "lucide-react";
import { useIndTracker } from "./utilities";
import { MeetingEditorAgendaModuleLoader } from "./MeetingEditorAgendaModule";
import { useGetAgenda } from "@/hooks/queryHooks/agendaHooks";
import { agendaRepo, type AgendaDto } from "@/repositories/agendaRepo";
import type { AgendaModuleDto } from "@/repositories/agendaModuleRepo";

// #region Agenda Loader

/** Loaders are necessary to load the object's data and only render the actual component when it
 * finishes loading because useImmediateMutationSystem requires the data be immediately
 * available in the render function. */

export const MeetingEditorAgendaLoader = ({
  agendaId,
}: {
  agendaId: string;
}) => {
  const { tenant } = useMeetingEditor();
  const { data: agenda } = useGetAgenda(tenant.id, agendaId);

  if (!agenda) return <p>...</p>;
  return <MeetingEditorAgenda agenda={agenda} />;
};

// #endregion

// #region Agenda

export const MeetingEditorAgenda = ({
  agenda: _agenda,
}: {
  agenda: AgendaDto;
}) => {
  const {
    tenant,
    mutationSystem,
    viewOnly,
    openEditForm,
    openLoadForm,
    undoStack,
  } = useMeetingEditor();

  const mutationKey = "agenda:" + _agenda.id;
  const tenantId = tenant.id;
  const agendaMutation: Mutator<AgendaDto> = useFutureCallback(
    async (id, data) => {
      await agendaRepo.update(tenantId, id, data);
    }
  );
  const agenda = useImmediateMutationSystem(
    mutationSystem,
    mutationKey,
    _agenda
  );

  const handleDragEnd = (evt: DragEndEvent) => {
    const { active, over } = evt;

    const activeData = active?.data.current as DragData | undefined;
    const overData = over?.data.current as DragData | undefined;

    const insert: DragInsertPos = dragInsertPos(active, over);

    if (
      // if drag is not active...
      !active ||
      !over ||
      !activeData ||
      !overData ||
      // or if user's dropping item back where it was...
      (activeData.id === overData.id && activeData.type === overData.type)
    ) {
      return; // ...then bail
    }

    if (activeData.type === "agendaModule") {
      // dragging a section
      if (overData.type !== "agendaModule") return;

      const items = agenda.agendaModuleIds;
      const agendaModuleIds = arrayMove(
        items,
        activeData.index,
        overData.index + +(insert === "after")
      );

      const prev = items.slice();
      const next = agendaModuleIds.slice();
      const agendaId = agenda.id;

      // push delta to undo stack
      undoStack.push({
        label: "Move Section",
        undo() {
          mutationSystem.debounce(
            mutationKey,
            agendaMutation,
            { agendaModuleIds: prev },
            agendaId
          );
        },
        redo() {
          mutationSystem.debounce(
            mutationKey,
            agendaMutation,
            { agendaModuleIds: next },
            agendaId
          );
        },
      });

      // update sort order
      mutationSystem.debounce(
        mutationKey,
        agendaMutation,
        { agendaModuleIds },
        agenda.id
      );
    } else if (activeData.type === "agendaItem") {
      // dragging an item
      // determine index and container item is coming from and going to
      const fromContainer = activeData.container;
      const toContainer =
        overData.type === "agendaModule" ? overData.id : overData.container;
      const fromIndex = activeData.index;
      const toIndex =
        overData.type === "agendaModule"
          ? 0
          : overData.index + +(insert === "after");

      if (fromContainer === toContainer) {
        // if item is moving within the same container, update using arrayMove
        const updateOrder = activeData.updateOrder;

        let prev: string[] = [];
        let next: string[] = [];

        updateOrder((agendaItemIds) => {
          prev = agendaItemIds;
          next = arrayMove(agendaItemIds, fromIndex, toIndex);
          return next;
        });

        // push delta to undo stack
        undoStack.push({
          label: "Move Agenda Item",
          undo() {
            updateOrder(() => prev);
          },
          redo() {
            updateOrder(() => next);
          },
        });
      } else {
        // if item is moving from one container to another, update by ...
        const movedId = activeData.id;
        const movedInd = activeData.ind;
        const fromUpdateOrder = activeData.updateOrder;
        const toUpdateOrder = overData.updateOrder;

        const prev = { from: [] as string[], to: [] as string[] };
        const next = { from: [] as string[], to: [] as string[] };

        // ... removing ...
        fromUpdateOrder((agendaItemIds) => {
          prev.from = agendaItemIds.slice();
          let ind = 0;
          next.from = agendaItemIds.filter(
            (id) => id !== movedId || ind++ !== movedInd
          );
          return next.from;
        });
        // ... then inserting
        toUpdateOrder((agendaItemIds) => {
          prev.to = agendaItemIds.slice();
          next.to = prev.to.slice();
          next.to.splice(toIndex, 0, movedId);
          return next.to;
        });

        // push delta to undo stack
        undoStack.push({
          label: "Move Agenda Item",
          undo() {
            fromUpdateOrder(() => prev.from);
            toUpdateOrder(() => prev.to);
          },
          redo() {
            fromUpdateOrder(() => next.from);
            toUpdateOrder(() => next.to);
          },
        });
      }
    } else if (activeData.type === "document") {
      // dragging a document
      if (overData.type !== "document") return;
      // must be in the same container
      if (overData.container !== activeData.container) return;

      const updateOrder = activeData.updateOrder;

      let prev: string[] = [];
      let next: string[] = [];

      const fromIndex = activeData.index;
      const toIndex = overData.index + +(insert === "after");

      updateOrder((documentIds) => {
        prev = documentIds;
        next = arrayMove(documentIds, fromIndex, toIndex);
        return next;
      });

      // push delta to undo stack
      undoStack.push({
        label: "Move Document",
        undo() {
          updateOrder(() => prev);
        },
        redo() {
          updateOrder(() => next);
        },
      });
    }
  };

  const agendaId = agenda.id;
  const handleAddAgendaModule = useFutureCallback(
    async (afterAgendaModuleId: string | null, load: boolean) => {
      let newAgendaModule: AgendaModuleDto | null = null;
      if (load) {
        newAgendaModule = await openLoadForm<AgendaModuleDto>("agendaModule");
        if (newAgendaModule?.template)
          throw new Error("Failed to copy template section");
      } else {
        newAgendaModule = await openEditForm<AgendaModuleDto>(
          "agendaModule",
          null
        );
      }

      if (!newAgendaModule) return;

      const prev = agenda.agendaModuleIds.slice();
      const next = prev.slice();

      if (afterAgendaModuleId) {
        const index = prev.indexOf(afterAgendaModuleId) + 1;
        next.splice(index, 0, newAgendaModule.id);
      } else {
        next.push(newAgendaModule.id);
      }

      mutationSystem.debounce(
        mutationKey,
        agendaMutation,
        { agendaModuleIds: next },
        agendaId
      );

      // push delta to undo stack
      undoStack.push({
        label: load ? "Add Template Section" : "Add New Section",
        undo() {
          mutationSystem.debounce(
            mutationKey,
            agendaMutation,
            { agendaModuleIds: prev },
            agendaId
          );
        },
        redo() {
          mutationSystem.debounce(
            mutationKey,
            agendaMutation,
            { agendaModuleIds: next },
            agendaId
          );
        },
      });
    }
  );

  const handleCreateAgendaModule = useFutureCallback(() =>
    handleAddAgendaModule(null, false)
  );

  const handleLoadAgendaModule = useFutureCallback(() =>
    handleAddAgendaModule(null, true)
  );

  const handleRemoveAgendaModule = useFutureCallback(
    (agendaModuleId: string) => {
      const prev = agenda.agendaModuleIds.slice();
      const next = prev.filter((id) => id !== agendaModuleId);

      mutationSystem.debounce(
        mutationKey,
        agendaMutation,
        { agendaModuleIds: next },
        agendaId
      );

      // push delta to undo stack
      undoStack.push({
        label: "Remove Agenda Section",
        undo() {
          mutationSystem.debounce(
            mutationKey,
            agendaMutation,
            { agendaModuleIds: prev },
            agendaId
          );
        },
        redo() {
          mutationSystem.debounce(
            mutationKey,
            agendaMutation,
            { agendaModuleIds: next },
            agendaId
          );
        },
      });
    }
  );

  const { getInd, trackInd } = useIndTracker();

  return (
    <DndContext onDragEnd={handleDragEnd} collisionDetection={pointerWithin}>
      <div>
        {agenda.agendaModuleIds.map((agendaModuleId, index) => (
          <MeetingEditorAgendaModuleLoader
            key={agendaModuleId + ":" + getInd(agendaModuleId)}
            ind={trackInd(agendaModuleId)}
            containerId={agendaId}
            index={index}
            agendaModuleId={agendaModuleId}
            onAdd={handleAddAgendaModule}
            onRemove={handleRemoveAgendaModule}
          />
        ))}
        {!agenda.agendaModuleIds.length && !viewOnly && (
          <div className="flex flex-row gap-4 mt-10">
            <button
              type="button"
              className="text-blue-500 flex flex-row gap-1 items-center hover:text-blue-400 focus-visible:text-blue-400 relative active:top-[1px]"
              onClick={handleCreateAgendaModule}
            >
              <PlusIcon className="w-5 inline-block" /> Create New Section
            </button>
            <button
              type="button"
              className="text-blue-500 flex flex-row gap-1 items-center hover:text-blue-400 focus-visible:text-blue-400 relative active:top-[1px]"
              onClick={handleLoadAgendaModule}
            >
              <ArrowDownIcon className="w-5 inline-block" /> Add Template
              Section
            </button>
          </div>
        )}
      </div>
      <EditorDragOverlay />
    </DndContext>
  );
};

// #endregion
